import type { LaunchDarklyFlags } from '@getaccept/lib-shared/src/launch-darkly/types/launch-darkly-flags';
import type { PricingTableColumn, ColumnType } from '../../types/pricing-table';
import { Align, NumberInputType, EPricingTableColumn } from '../../types/enums';
import { nonGridColumns } from '../../constants/pricing-table';

export const isColumnEnabled = (column: PricingTableColumn | undefined) => column?.enabled || false;

export const isColumnDisabled = (column: PricingTableColumn | undefined) =>
  !isColumnEnabled(column);

export const isGridColumn = (column: PricingTableColumn | undefined) =>
  column
    ? !nonGridColumns.includes(column.name as EPricingTableColumn) || !!column.productPropertyId
    : false;

export const isProductPropertyColumn = (column: PricingTableColumn | undefined) =>
  !!column?.productPropertyId;

export const isCustomColumn = (column: PricingTableColumn | undefined) => !!column?.isCustom;

export const isPredefinedColumn = (column: PricingTableColumn | undefined) =>
  column ? !isProductPropertyColumn(column) && !isCustomColumn(column) : false;

export const getColumnType = (column: PricingTableColumn | undefined): ColumnType => {
  if (!column) return 'Text';

  if (column.type) {
    return column.type;
  }

  if (column.isCustom) {
    return 'Text';
  }

  switch (column.name) {
    case EPricingTableColumn.Name:
    case EPricingTableColumn.Description:
    case EPricingTableColumn.Sku:
      return 'Text';
    case EPricingTableColumn.Price:
    case EPricingTableColumn.Units:
    case EPricingTableColumn.Tax:
    case EPricingTableColumn.TotalPrice:
    case EPricingTableColumn.NetPrice:
    case EPricingTableColumn.Discount:
      return 'Number';
  }

  return 'Text';
};

export const getColumnNumberInputType = (
  column: PricingTableColumn | undefined
): NumberInputType => {
  if (!column) {
    return NumberInputType.Number;
  }

  switch (column?.unit) {
    case 'Currency':
      return NumberInputType.Currency;
    case 'Percentage':
      return NumberInputType.Percentage;
    case 'Unknown':
      return NumberInputType.Number;
    default:
  }

  switch (column?.name) {
    case EPricingTableColumn.Price:
    case EPricingTableColumn.TotalPrice:
      return NumberInputType.Currency;
    case EPricingTableColumn.Tax:
      return NumberInputType.Percentage;
    default:
      return NumberInputType.Number;
  }
};

export const getColumnAlignment = (column: PricingTableColumn | undefined): Align =>
  getColumnType(column) === 'Number' ? Align.Right : Align.Left;

export const isFormattableTextTypeColumn = (
  column: PricingTableColumn | undefined,
  flags: LaunchDarklyFlags
) =>
  flags.textFormattingInCustomColumns
    ? !!column &&
      getColumnType(column) === 'Text' &&
      ![EPricingTableColumn.Name, EPricingTableColumn.Sku].includes(
        column?.name as EPricingTableColumn
      )
    : column?.name === EPricingTableColumn.Description;
